<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { onSmartCardKeyPress } from '@/composables/hardware/smartcard'
import { $bus } from '@/common/events'
//https://jsitor.com/UO5g8LGOw

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: null
  },
  placeholder: {
    type: String,
    default: ""
  },
  enabled: {
    type: Boolean,
    default: true
  },
  display: {
    type: Boolean,
    default: true
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  iconic: {
    type: Boolean,
    default: true
  },
  masked: {
    type: Boolean,
    default: false
  },
  minValue: {
    type: Number,
    default: null
  },
  maxValue: {
    type: Number,
    default: null
  },
  maxLength: {
    type: Number,
    default: 12
  },
  clearMode: {
    type: String,
    default: "clear" //clear, backspace
  },
  error: {
    type: Boolean,
    default: false
  },
  modelValue: {
  },
  initialStep: { //Delay between first press and next counting step
    type: Number,
    default: 300
  },
  normalStep: { //Delay between every consecutive counting step
    type: Number,
    default: 150
  },
  fastStep: { //Delay between every consecutive counting step when switched to fast mode
    type: Number,
    default: 50
  },
  fastAfter: { //Delay from initial press to shift to fast mode
    type: Number,
    default: 800
  },
});

const emit = defineEmits([ "confirm", "keypress", "keypress2", "keydown", "keyup", "mousedown", "update:modelValue" ]);

const type = computed(() => props.masked == true ? "password" : "text");

const value = computed({
  get() {
    return props.modelValue || "";
  },
  set(value) {
    emit("update:modelValue", value);
  }
});

const numbers = ref([
  7, 8, 9,
  4, 5, 6,
  1, 2, 3
]);

const isKeypressIgnored = ref(false);

onMounted(() => {
});

onUnmounted(() => {
});

onSmartCardKeyPress((data) => {
  let key = data.key;
  console.log("onSmartCardKeyPress", key);
  switch (key) {
    case 27: //Wildcard (*), back, delete, clear
      keypress(":clear");
      break;

    case 13: //Hash (#), bell, enter, confirm
      keypress(":ok");
      break;

    default:
      keypress(key);
      break;
  }
});

function ignoreKeypress() {
  isKeypressIgnored.value = true;
  setTimeout(() => isKeypressIgnored.value = false, 300);
}

function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

let lastKeyData = null;
let initialTimer = null;
let normalTimer = null;
let fastTimer = null;
let pressed = false;

function repeatLastKey() {
  if (lastKeyData) {
    emit("keypress", lastKeyData);
    emit("keydown", lastKeyData);
  }
}

function startStepTimer() {
  //Normal interval steps
  if (props.normalStep > 0) {
    normalTimer = setInterval(function() {
      if (pressed) {
        repeatLastKey();
      }
    }, props.normalStep);
  }

  //Shift to speed up steps
  if (props.fastAfter > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        //repeatLastKey();
        startFastTimer();
      }
    }, props.fastAfter);
  }
}

function startFastTimer() {
  if (props.fastStep > 0) {
    if (normalTimer) {
      clearInterval(normalTimer);
      normalTimer = null;
    }

    fastTimer = setInterval(function() {
      if (pressed) {
        repeatLastKey();
      }
    }, props.fastStep);
  }
}

function start() {
  //repeatLastKey();
  pressed = true;
  if (props.initialStep > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        repeatLastKey();
        startStepTimer();
      }
    }, props.initialStep);
  }
  else {
    startStepTimer();
  }
}

function end() {
  pressed = false;
  if (initialTimer) {
    clearTimeout(initialTimer);
    initialTimer = null;
  }
  if (normalTimer) {
    clearInterval(normalTimer);
    normalTimer = null;
  }
  if (fastTimer) {
    clearInterval(fastTimer);
    fastTimer = null;
  }
}

function keypress(e) {
  let n = e && typeof e == "string" ? e : null;
  if (!n) {
    n = e.target.dataset.char;
  }
  emit("keypress", n);

  //Emit the keypress event
  emit("keypress2", getKeyData(e));

  if (n == ":clear") {
    value.value = "";
    return;
  }
  if (n == ":backspace") {
    value.value = value.value?.substring(0, value.value.length - 1) || "";
    return;
  }
  if (n == ":ok") {
    emit("confirm");
    return;
  }
  if (value.value.length >= props.maxLength) {
    return ignoreKeypress();
  }
  if (isNumeric(props.minValue)) {
    let i = parseInt(value.value + n);
    if (i < props.minValue) {
      return ignoreKeypress();
    }
  }
  if (isNumeric(props.maxValue)) {
    let i = parseInt(value.value + n);
    if (i > props.maxValue) {
      return ignoreKeypress();
    }
  }

  //Append digit
  value.value += n;
}

function keydown(e) {
  emit("keydown", getKeyData(e));
  lastKeyData = getKeyData(e);
  start();
}

function keyup(e) {
  end();
  emit("keyup", getKeyData(e));
}

function isAlphanumeric(char) {
  return /^[\w\dčćđšž]{1}$/gi.test(char);
}

function getKeyData(e) {
  let modifier = false;
  let ctrlAltShift = {
    ctrl: false,
    alt: false,
    shift: false,
    meta: false
  };

  let char = e.target.dataset.char; //Normal version
  let special = char && char.startsWith(":") ? true : false;

  //Toggle lowercase/uppercase/alternative letter due to the shift key and/or caps lock key
  let alphanumeric = isAlphanumeric(char);

  return {
    char, //Pressed key
    alphanumeric, //Is press key an alphanumeric char (A-Z, 0-9) ? true or false
    special, //Is press key a special key such as enter or backspace? true or false
    modifier, //Is press key a modifier key such as ctrl/alt/delete? true or false
    modifiers: ctrlAltShift //Current state of modifier keys
  };
}
</script>

<template>
  <div class="keyboard" @mousedown.prevent="() => emit('mousedown')">
    <div class="display" v-if="props.display">
      <input :type="type" :id="props.id" v-model="value" :placeholder="props.placeholder" :required="!!props.required" :readonly="!!props.readonly" inputmode="none"
        class="textbox col-span-3" :class="{ 'textbox-blank': !value || value.length == 0, 'textbox-ignore-keypress': isKeypressIgnored, 'textbox-error': props.error }" />
    </div>

    <!-- Numeric buttons -->
    <button v-for="n of numbers" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="n">{{ n }}</button>

    <!-- Bottom row buttons -->
    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":backspace" v-if="props.clearMode == 'backspace'"><i class="fa fa-arrow-left text-red-500"></i></button>
    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":clear" v-if="props.clearMode != 'backspace' && !props.iconic"><span class="text-red-500">C</span></button>
    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":clear" v-if="props.clearMode != 'backspace' && props.iconic"><i class="fa fa-xmark text-red-500"></i></button>

    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char="0">0</button>
    
    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":ok" v-if="!props.iconic"><span class="text-green-500">OK</span></button>
    <button @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":ok" v-if="props.iconic"><i class="fa fa-check text-green-500"></i></button>

  </div>
</template>

<style scoped>
.textbox {
  @apply block border rounded-lg p-2.5 shadow-inner transition-all duration-200
    text-center text-4xl bg-transparent tracking-wider select-auto outline-none
    bg-blue-100 border-blue-300 text-blue-600
    dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400;
}

.textbox.textbox-blank {
  @apply bg-slate-100 border-slate-300
    dark:bg-gray-800 dark:border-gray-600;
}

.textbox.textbox-ignore-keypress {
  @apply bg-blue-200/70 dark:bg-blue-800;
}

.textbox.textbox-error {
  @apply border-red-400 bg-red-200 text-red-700;
}

.screen {
  @apply h-screen min-w-[400px] max-w-sm 
  mx-auto flex items-center;
}

.keyboard {
  @apply grid grid-cols-3 grid-rows-5 gap-1 
    w-full h-96 font-bold rounded-lg 
    text-white select-none;
    /* ring-2 ring-white/20 */
}

.keyboard .display {
  @apply col-span-3 row-span-1 rounded-lg grid grid-cols-3 gap-1 place-content-center;
}

.keyboard button {
  @apply relative flex items-center justify-center
    transition-all duration-500 rounded
    active:scale-95 active:duration-[0s] active:bg-blue-100
    border rounded-lg shadow text-2xl font-bold tracking-tight
    bg-white text-gray-900 border-gray-200 hover:bg-gray-100
    dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700;
  /* active:bg-amber-500 */
}
</style>
